import PopUpBase from '@components/PopUpBase';
import PopUpWarning from '@components/PopUpWarning';
import { pushError, pushSuccess } from '@components/toast';
import { cameraTaskAttachmentHttpController } from '@controllers/index';
import { Paging } from '@Core';
import { CameraTask } from '@EcamModel/model/CameraTask';
import { CameraTaskAttachment } from '@EcamModel/model/CameraTaskAttachment';
import usePopUp, { IPopUp } from '@hooks/usePopUp';
import { Box, Button, CircularProgress, Dialog, Stack, TextField, Typography } from '@mui/material';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import { ellipsisStyles } from '@pages/task-management';
import { blobURLCameraAttachmentsPhoto } from '@pages/task-management/config';
import color from '@theme/Colors';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { formatDateTime } from 'src/helpers';
import { useBackdrop } from 'src/providers/BackdropProvider';
import 'swiper/css';
import 'swiper/css/scrollbar';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ActionButtonsGenerics } from '../ActionButtonsGenerics';

type Props = IPopUp & {
    taskItem: CameraTask;
    isFetchingListTaskAttachments: boolean;
    listTaskAttachments: Paging<CameraTaskAttachment> | undefined;
    isLoadingListTaskAttachments: boolean;
};
export default function PopUpCountAttachments(props: Props) {
    const {
        open,
        taskItem,
        isLoadingListTaskAttachments,
        isFetchingListTaskAttachments,
        listTaskAttachments,
        onClose,
    } = props;
    const queryClient = useQueryClient();
    const backdrop = useBackdrop();
    const popupConfirmDeleteAttachmentsFile = usePopUp();

    const [descriptionItem, setDescriptionItem] = useState<CameraTaskAttachment>({} as CameraTaskAttachment);
    const [descriptionId, setDescriptionId] = useState<number | null>(null);
    const [descriptionValue, setDescriptionValue] = useState<string>('');
    const [selectedImgPreview, setSelectedImgPreview] = useState<string | null>(null);
    const [openImgPreview, setOpenImgPreview] = useState<boolean>(false);
    const [loadingState, setLoadingState] = useState<boolean>(false);

    const isDisabled = useMemo(() => {
        return descriptionValue !== descriptionItem.Desc;
    }, [descriptionItem, descriptionValue]);

    const isDescriptionLength = descriptionValue.length > 255;

    const handleCancel = () => {
        onClose?.();
        handleCancelChangeDescription();
    };

    const handleCancelChangeDescription = () => {
        setDescriptionItem({} as CameraTaskAttachment);
    };

    const handleSetAttachmentsDescription = (fileId: number) => {
        if (fileId && isDisabled && !isDescriptionLength) {
            backdrop.setTrue();
            cameraTaskAttachmentHttpController
                .upsert({ Desc: descriptionValue.trim() || '', Id: fileId })
                .then((res) => {
                    queryClient.setQueryData<Paging<CameraTaskAttachment> | undefined>(
                        ['listTaskAttachments', taskItem.Id],
                        (oldData) => {
                            if (!oldData || !oldData.rows) return oldData;
                            const updatedRows = oldData.rows.map((item) =>
                                item.Id === fileId ? { ...item, Desc: res.Desc } : item
                            );

                            return {
                                ...oldData,
                                rows: updatedRows,
                            };
                        }
                    );

                    pushSuccess('Change description for attachment successfully');
                })
                .catch((err) => pushError(err.message))
                .finally(() => {
                    setDescriptionItem({} as CameraTaskAttachment);
                    backdrop.setFalse();
                });
        } else {
            handleCancelChangeDescription();
        }
    };

    const handleKeyboardEvent = (e: KeyboardEvent<HTMLDivElement>, fileId: number) => {
        if (e.key === 'Enter') {
            handleSetAttachmentsDescription(fileId);
        } else if (e.key === 'Escape') {
            handleCancelChangeDescription();
        }
    };

    // Confirm delete task attachments
    const handleConfirmDeleteAttachmentsFile = () => {
        setLoadingState(true);
        cameraTaskAttachmentHttpController
            .delete(descriptionId?.toString()!)
            .then(async (_) => {
                queryClient.invalidateQueries(['listTaskAttachments']);
                queryClient.invalidateQueries(['listTask']);
                pushSuccess('Deleted attachment successfully');
                popupConfirmDeleteAttachmentsFile.onClose();
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            })
            .finally(() => {
                setLoadingState(false);
            });
    };

    useEffect(() => {
        if (!open) return;
        setDescriptionValue(descriptionItem?.Desc || '');
    }, [open, descriptionItem]);

    return (
        <PopUpBase
            title={
                <Typography
                    variant="h4"
                    sx={{
                        ...ellipsisStyles,
                    }}
                >
                    View task attachments: {taskItem.TaskName ?? ''}
                </Typography>
            }
            fixOverflow
            dialogProps={{
                fullWidth: true,
                maxWidth: 'md',
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            onClickClose={handleCancel}
            open={open}
            onClose={onClose}
            hideConfirm
            minWidthButton={130}
            desc={
                <Stack
                    direction={'column'}
                    spacing={2}
                    bgcolor={color.grey100}
                    borderRadius={'4px'}
                    p={2}
                    width={'100%'}
                >
                    {isFetchingListTaskAttachments || isLoadingListTaskAttachments ? (
                        <Stack direction="row" justifyContent="center" alignItems="center" minHeight={500}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <>
                            {listTaskAttachments?.rows.length && listTaskAttachments?.rows.length > 0 ? (
                                <Swiper
                                    spaceBetween={16}
                                    slidesPerView={listTaskAttachments?.rows.length > 1 ? 1.1 : 1}
                                >
                                    {listTaskAttachments?.rows.map((desc) => {
                                        const isEditing = descriptionItem?.Id === desc.Id;
                                        return (
                                            <SwiperSlide key={desc.Id}>
                                                <Stack direction={'column-reverse'} spacing={2}>
                                                    <Stack direction={'row'} width={'100%'}>
                                                        <Box width={'15%'}>
                                                            <Typography
                                                                component={'span'}
                                                                lineHeight={isEditing ? '40px' : 0}
                                                            >
                                                                Description:
                                                            </Typography>
                                                        </Box>
                                                        <Box width={isEditing ? '75%' : '80%'} textAlign={'start'}>
                                                            {isEditing ? (
                                                                <TextField
                                                                    fullWidth
                                                                    autoFocus
                                                                    placeholder={'Enter description'}
                                                                    label="Description"
                                                                    value={descriptionValue}
                                                                    onKeyDown={(e) => handleKeyboardEvent(e, desc.Id!)}
                                                                    onChange={(e) =>
                                                                        setDescriptionValue(e.target.value)
                                                                    }
                                                                    error={isDescriptionLength}
                                                                    helperText={
                                                                        isDescriptionLength
                                                                            ? `Description cannot exceed 255 characters. (${descriptionValue.length}/255)`
                                                                            : `${descriptionValue.length}/255`
                                                                    }
                                                                />
                                                            ) : (
                                                                <Typography
                                                                    component={'span'}
                                                                    onDoubleClick={() => setDescriptionItem(desc)}
                                                                >
                                                                    {desc.Desc}
                                                                </Typography>
                                                            )}
                                                        </Box>

                                                        <ActionButtonsGenerics
                                                            isEditing={isEditing}
                                                            isDisabled={isDisabled && !isDescriptionLength}
                                                            item={desc}
                                                            condition={!desc.Desc || desc.Desc === ''}
                                                            onSave={(id) => handleSetAttachmentsDescription(id)}
                                                            onClose={() =>
                                                                setDescriptionItem({} as CameraTaskAttachment)
                                                            }
                                                            onUpsert={(item) => setDescriptionItem(item)}
                                                            onDelete={(id) => setDescriptionId(id)}
                                                            popupConfirmDelete={popupConfirmDeleteAttachmentsFile}
                                                        />
                                                    </Stack>

                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '450px',
                                                            background: `url(${blobURLCameraAttachmentsPhoto}${desc?.BlobName}) no-repeat center center`,
                                                            backgroundSize: 'cover',
                                                            borderRadius: 1,
                                                            position: 'relative',
                                                            cursor: 'grab',
                                                            '&:active': {
                                                                cursor: 'grabbing',
                                                            },
                                                        }}
                                                        onClick={() => {
                                                            setSelectedImgPreview(
                                                                `${blobURLCameraAttachmentsPhoto}${desc?.BlobName}`
                                                            );
                                                            setOpenImgPreview(true);
                                                        }}
                                                    >
                                                        <Box
                                                            position={'absolute'}
                                                            zIndex={1}
                                                            bottom={0}
                                                            left={0}
                                                            bgcolor={'rgba(0, 0, 0, 0.5)'}
                                                            textAlign={'center'}
                                                            borderRadius={'0px 4px 0px 4px'}
                                                            p={'2px 6px'}
                                                        >
                                                            <Typography component={'span'} color={color.white}>
                                                                {formatDateTime(desc.Created!)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Stack>
                                            </SwiperSlide>
                                        );
                                    })}
                                </Swiper>
                            ) : (
                                <ListNoData />
                            )}
                        </>
                    )}

                    {selectedImgPreview && (
                        <Dialog
                            open={!!openImgPreview}
                            onClose={() => {
                                setOpenImgPreview(false);
                                setSelectedImgPreview(null);
                            }}
                            PaperProps={{
                                sx: {
                                    minWidth: 1280,
                                    minHeight: 720,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    minWidth: 1280,
                                    minHeight: 720,
                                    background: `url(${selectedImgPreview}) no-repeat center center`,
                                    backgroundSize: 'cover',
                                    borderRadius: '4px',
                                    objectFit: 'contain',
                                }}
                            />
                        </Dialog>
                    )}

                    <PopUpWarning
                        {...popupConfirmDeleteAttachmentsFile}
                        isLoading={loadingState}
                        title="Confirm"
                        message={`Are you sure you want to delete this attachment?`}
                        onClose={() => {
                            setDescriptionItem({} as CameraTaskAttachment);
                            popupConfirmDeleteAttachmentsFile.onClose();
                        }}
                        onConfirm={handleConfirmDeleteAttachmentsFile}
                    />
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        variant="cancel"
                        onClick={() => {
                            handleCancel();
                        }}
                    >
                        Close
                    </Button>
                </Stack>
            }
        />
    );
}
